import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './OTPInput.css';

const OTPInput = ({ value, onChange, onComplete, error, disabled, length = 6 }) => {
  const [otp, setOtp] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Initialize from value prop if provided
    if (value && value.length <= length) {
      const digits = value.split('').concat(Array(length - value.length).fill(''));
      setOtp(digits.slice(0, length));
    } else if (!value) {
      setOtp(Array(length).fill(''));
    }
  }, [value, length]);

  useEffect(() => {
    // Focus first empty input
    const firstEmptyIndex = otp.findIndex(digit => digit === '');
    if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
      inputRefs.current[firstEmptyIndex].focus();
    }
  }, [otp]);

  const handleChange = (index, newValue) => {
    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = newValue;
    setOtp(newOtp);

    // Call onChange with full OTP string
    const otpString = newOtp.join('');
    onChange(otpString);

    // Auto-focus next input
    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete when all digits are entered
    if (otpString.length === length && onComplete) {
      onComplete(otpString);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only process if pasted data matches the required length
    const regex = new RegExp(`^\\d{${length}}$`);
    if (regex.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      onChange(pastedData);
      if (onComplete) {
        onComplete(pastedData);
      }
      // Focus last input
      inputRefs.current[length - 1]?.focus();
    }
  };

  const handleFocus = (index) => {
    // Select all text when focused
    inputRefs.current[index]?.select();
  };

  return (
    <div className="otp-input-container">
      <div className="otp-inputs">
        {otp.map((digit, index) => (
          <motion.input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            className={`otp-input-box ${error ? 'error' : ''} ${digit ? 'filled' : ''}`}
            disabled={disabled}
            autoComplete="off"
            aria-label={`OTP digit ${index + 1}`}
            whileFocus={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          />
        ))}
      </div>
      {error && (
        <AnimatePresence>
          <motion.div
            className="otp-error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <span className="error-icon">⚠</span>
            <span>{error}</span>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default OTPInput;